/**
 * Central API client for deepnets frontend.
 *
 * SPA session token lifecycle (Turnstile-only):
 *   - Cloudflare Turnstile (invisible) verifies the browser in the background and
 *     is exchanged for a 24h session token, cached in localStorage.
 *   - Return visits within 24h reuse the cached token — no Turnstile, no wait.
 *   - Only a first visit / expired token pays the Turnstile round-trip (bounded).
 *   - apiFetch() self-heals a stale token (one refresh + retry on 403).
 *   - A challenge that lands after the soft deadline is still adopted for the rest
 *     of the session rather than discarded.
 *
 * The old client-side proof-of-work "challenge/grace" path was removed: the solver
 * shipped in the bundle, so it gave bots free ongoing access, while its 15s TTL +
 * per-IP challenge rate limit forced real users onto the slow Turnstile fallback
 * (the source of the list-loading hang on cached return visits).
 */

import { useState, useEffect } from 'react';
import { posthogCapture } from './posthog';

const API_HOST = process.env.REACT_APP_DEEPNETS_API_HOST || 'https://spa-api.deepnets.ai';
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';
// Two deadlines, deliberately different (see getTurnstileToken):
//   SOFT — how long apiFetch will block before proceeding without a token.
//   HARD — when we abandon the widget entirely.
// A single 8s deadline that also tore the widget down was discarding tokens that
// were about to arrive: Cloudflare's p75 verification is ~7.1s on Android and
// ~7.6s on Windows (vs ~3.5s on iOS), so 60-85% of fresh-token attempts on those
// platforms timed out, and the page then ran unauthenticated for its whole life.
const TURNSTILE_SOFT_WAIT_MS = 8000;
const TURNSTILE_TIMEOUT_MS = 25000;
const TURNSTILE_SCRIPT_TIMEOUT_MS = 5000;

// ── Token-acquisition instrumentation ──────────────────────────────────
// Emits one `spa_token_ready` per page load: source=cache|turnstile|failed + ms.
// Feeds a PostHog insight quantifying how often users wait for a token and for
// how long (cache-hit rate = fraction of loads with NO Turnstile wait).
//
// `failed` additionally carries a `reason`. Without it, six unrelated causes —
// a rejected challenge (the only genuine bot signal), a slow challenge, a blocked
// script, a server rejection — all read as one undifferentiated bucket, which is
// how a 69% failure rate ran for a week without being visible.
type TokenFailureReason =
  | 'no_sitekey'         // REACT_APP_TURNSTILE_SITE_KEY not set in the build
  | 'script_error'       // challenges.cloudflare.com blocked, or load timed out
  | 'challenge_rejected' // Turnstile error-callback — the real "you're a bot" verdict
  | 'challenge_timeout'  // widget never answered inside the soft window
  | 'server_rejected'    // /api/spa/session refused the Turnstile token
  | 'network_error';     // /api/spa/session unreachable

let lastFailureReason: TokenFailureReason | null = null;

const moduleStart = Date.now();
let tokenReadyEmitted = false;
function emitTokenReady(source: 'cache' | 'turnstile' | 'failed', ms = 0, reason?: TokenFailureReason): void {
  if (tokenReadyEmitted) return;
  tokenReadyEmitted = true;
  try {
    posthogCapture('spa_token_ready', {
      source,
      ms,
      sinceLoadMs: Date.now() - moduleStart,
      ...(reason ? { reason } : {}),
    });
  } catch { /* analytics never breaks the app */ }
}

// ── SPA token state ────────────────────────────────────────────────────
const STORAGE_KEY = 'deepnets_spa_token';
const STORAGE_EXPIRY_KEY = 'deepnets_spa_token_expires';

let spaToken: string | null = null;
let spaTokenExpiresAt: number = 0;
let tokenPromise: Promise<void> | null = null;
let widgetInFlight = false;   // guards against concurrent Turnstile renders (see getTurnstileToken)

// Hydrate from localStorage on load
(function restoreCachedToken() {
  try {
    const cached = localStorage.getItem(STORAGE_KEY);
    const cachedExpiry = localStorage.getItem(STORAGE_EXPIRY_KEY);
    if (cached && cachedExpiry) {
      const expiry = Number(cachedExpiry);
      if (expiry > Date.now() + 5 * 60 * 1000) {
        spaToken = cached;
        spaTokenExpiresAt = expiry;
      } else {
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(STORAGE_EXPIRY_KEY);
      }
    }
  } catch {
    // localStorage unavailable
  }
})();

// ── Turnstile ──────────────────────────────────────────────────────────
let turnstileReady: Promise<void> | null = null;

function loadTurnstileScript(): Promise<void> {
  if ((window as any).turnstile) return Promise.resolve();
  if (turnstileReady) return turnstileReady;

  const pending = new Promise<void>((resolve, reject) => {
    (window as any).onloadTurnstileCallback = () => resolve();
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onloadTurnstileCallback';
    script.async = true;
    // Without this, anything Turnstile throws reaches window.onerror as a bare
    // "Script error." with no stack, message or line — the browser withholds
    // detail for cross-origin scripts unless they opt in via CORS. Cloudflare
    // serves this file with `access-control-allow-origin: *` (verified), so the
    // attribute is safe; on an origin WITHOUT that header it would block the
    // load outright. This is the only cross-origin script the app loads.
    script.crossOrigin = 'anonymous';
    script.onerror = () => reject(new Error('Failed to load Turnstile'));
    document.head.appendChild(script);
    // Reject rather than resolve on timeout. Resolving blind reported success to
    // a caller that then found no `window.turnstile` and had no idea why.
    setTimeout(() => reject(new Error('Turnstile script load timed out')), TURNSTILE_SCRIPT_TIMEOUT_MS);
  });
  // Drop the cached promise on failure so a later attempt can retry instead of
  // inheriting one dead load for the rest of the page's life. The no-op catch
  // also keeps a rejection with no listener yet from surfacing as unhandled.
  pending.catch(() => { turnstileReady = null; });
  turnstileReady = pending;
  return pending;
}

// Preload Turnstile ONLY for users who will need a fresh token (no valid cached one),
// so the script is fetched + parsed off the critical path — shrinking the first-load
// acquisition wait. Cache-hit users (the majority) skip it, so we never load Turnstile
// for people who never verify. Runs once at module import, after restoreCachedToken.
if (TURNSTILE_SITE_KEY && !spaToken) { loadTurnstileScript().catch(() => {}); }

/**
 * Render the invisible Turnstile widget and wait for a token.
 *
 * Resolves at the SOFT deadline so apiFetch is never blocked longer than before,
 * but deliberately leaves the widget running until the HARD deadline. A token
 * that arrives in between is handed to `onLate` rather than dropped — previously
 * the timer set `settled = true`, so a genuine token arriving at 8.1s hit a
 * no-op `done()` and the visitor spent the rest of the page load 403-ing on every
 * gated call despite having passed the challenge.
 *
 * The soft timer starts before the script load, so it bounds the whole
 * acquisition (script + challenge), not just the widget.
 */
function getTurnstileToken(onLate: (token: string) => void): Promise<string | null> {
  if (!TURNSTILE_SITE_KEY) {
    lastFailureReason = 'no_sitekey';
    return Promise.resolve(null);
  }

  // Only one widget attempt at a time. The soft deadline releases the caller but
  // deliberately leaves the widget running, and ensureSpaToken clears tokenPromise
  // when that happens — so without this guard every apiFetch during the 8-25s
  // window starts its OWN render, and a page making N calls solves N challenges.
  // Measured ~7 per session before this, with 42 human sessions past 11.
  // The in-flight attempt publishes its token globally via onLate, so callers that
  // arrive meanwhile lose nothing by waiting for it rather than starting another.
  if (widgetInFlight) return Promise.resolve(null);
  widgetInFlight = true;

  return new Promise((resolve) => {
    let softSettled = false;
    let abandoned = false;
    let widgetEl: HTMLElement | null = null;

    // Turnstile tokens expire after 300s and the widget re-executes to replace
    // them, calling `callback` again each time — for as long as it stays mounted.
    // We exchange the token for a 24h SPA session immediately, so those refreshes
    // are pure waste: one extra POST /api/spa/session per active user every five
    // minutes (on the one endpoint deliberately exempt from rate limiting), and
    // every refresh looked like a fresh "late recovery". 81% of the gaps between
    // consecutive recovery events sat in the 240-360s band, which is what gave
    // this away. Tearing the widget down once we have a token stops the cycle.
    const teardown = () => {
      if (!widgetEl) return;
      const t = (window as any).turnstile;
      try { t?.remove(widgetEl); } catch { /* already gone */ }
      widgetEl = null;
    };

    const settleSoft = (t: string | null) => {
      if (softSettled) return;
      softSettled = true;
      resolve(t);
    };

    const softTimer = setTimeout(() => {
      lastFailureReason = 'challenge_timeout';
      settleSoft(null);
    }, TURNSTILE_SOFT_WAIT_MS);
    const hardTimer = setTimeout(() => {
      abandoned = true;
      widgetInFlight = false;
      teardown();
    }, TURNSTILE_TIMEOUT_MS);

    // Idempotent: we want exactly one token per attempt. teardown() should stop
    // the widget re-executing, but the callback closure outlives the element, so
    // don't rely on removal alone to prevent a second exchange.
    let tokenHandled = false;
    const accept = (token: string) => {
      if (tokenHandled) return;
      tokenHandled = true;
      clearTimeout(softTimer);
      clearTimeout(hardTimer);
      widgetInFlight = false;
      teardown();
      if (abandoned) return;
      if (softSettled) onLate(token); else settleSoft(token);
    };
    const fail = (reason: TokenFailureReason) => {
      clearTimeout(softTimer);
      clearTimeout(hardTimer);
      widgetInFlight = false;
      teardown();
      lastFailureReason = reason;
      settleSoft(null);
    };

    loadTurnstileScript().then(() => {
      const turnstile = (window as any).turnstile;
      if (!turnstile) { fail('script_error'); return; }

      let container = document.getElementById('turnstile-container');
      if (container) {
        // Only reset a container we've actually rendered into. Calling remove()
        // on a fresh one makes Turnstile log "Nothing to reset found for provided
        // container" — 2188 of those came from a single visitor's retry loop.
        try { turnstile.remove(container); } catch { /* nothing rendered yet */ }
      } else {
        container = document.createElement('div');
        container.id = 'turnstile-container';
        container.style.display = 'none';
        document.body.appendChild(container);
      }

      widgetEl = container;
      turnstile.render(container, {
        sitekey: TURNSTILE_SITE_KEY,
        // We only ever need one token; don't let the widget re-execute on expiry.
        'refresh-expired': 'never',
        callback: accept,
        'error-callback': () => fail('challenge_rejected'),
        'timeout-callback': () => fail('challenge_timeout'),
      });
    }).catch(() => fail('script_error'));
  });
}

// ── Token lifecycle ────────────────────────────────────────────────────
function cacheSpaToken(token: string, expiresAt: number): void {
  try {
    localStorage.setItem(STORAGE_KEY, token);
    localStorage.setItem(STORAGE_EXPIRY_KEY, String(expiresAt));
  } catch { /* localStorage unavailable */ }
}

/** Exchange a verified Turnstile token for a 24h SPA session token. */
async function exchangeForSpaToken(turnstileToken: string): Promise<boolean> {
  try {
    const res = await fetch(`${API_HOST}/api/spa/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ turnstileToken }),
    });
    if (!res.ok) { lastFailureReason = 'server_rejected'; return false; }
    const data = await res.json();
    spaToken = data.token;
    spaTokenExpiresAt = data.expiresAt;
    cacheSpaToken(data.token, data.expiresAt);
    return true;
  } catch {
    lastFailureReason = 'network_error';
    return false;
  }
}

/** Acquire a 24h session token via Turnstile (invisible, background, bounded). */
async function refreshSpaToken(): Promise<void> {
  const turnstileToken = await getTurnstileToken((lateToken) => {
    // The challenge finished after we'd already given up waiting. Exchange it
    // anyway: the token is valid, and adopting it means the rest of the session
    // is authenticated instead of 403-ing on every gated call. In-flight calls
    // that already failed are recovered by apiFetch's 403 self-heal.
    void exchangeForSpaToken(lateToken).then((ok) => {
      if (ok) {
        try {
          posthogCapture('spa_token_late_recovery', { sinceLoadMs: Date.now() - moduleStart });
        } catch { /* analytics never breaks the app */ }
      }
    });
  });
  if (!turnstileToken) return; // no token yet — a late one may still arrive above
  await exchangeForSpaToken(turnstileToken);
}

/** Ensure we have a valid SPA token, refreshing if needed */
export async function ensureSpaToken(): Promise<void> {
  if (spaToken && Date.now() < spaTokenExpiresAt - 2000) { emitTokenReady('cache'); return; }

  if (!tokenPromise) {
    const t0 = Date.now();
    tokenPromise = refreshSpaToken().finally(() => {
      tokenPromise = null;
      emitTokenReady(
        spaToken ? 'turnstile' : 'failed',
        Date.now() - t0,
        spaToken ? undefined : (lastFailureReason ?? 'network_error'),
      );
    });
  }
  await tokenPromise;
}

// ── Public API ─────────────────────────────────────────────────────────

/**
 * Authenticated fetch wrapper. Automatically attaches the SPA session token.
 *
 * On a first visit this DOES wait on Turnstile, bounded by TURNSTILE_SOFT_WAIT_MS
 * (the claim that it never blocks on Turnstile dated from the removed proof-of-work
 * path). Past that deadline it proceeds unauthenticated rather than hanging the UI;
 * if the challenge lands later the token is still adopted, and the 403 self-heal
 * below picks it up on the next call.
 */
/**
 * Attach standard SPA headers: x-spa-token + Bearer JWT (if present and the
 * caller didn't already set Authorization). Mutates the Headers in place.
 */
function applyStandardHeaders(headers: Headers): void {
  if (spaToken) {
    headers.set('x-spa-token', spaToken);
  }
  if (!headers.has('Authorization')) {
    const jwt = localStorage.getItem('deepnets_token');
    if (jwt) headers.set('Authorization', `Bearer ${jwt}`);
  }
}

export async function apiFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  await ensureSpaToken();

  const headers = new Headers(init?.headers);
  applyStandardHeaders(headers);

  let res = await fetch(input, { ...init, headers });

  // Self-heal a stale SPA token (e.g. returned to a long-cached tab): the server
  // rejected it — drop it, re-acquire once via Turnstile, and retry.
  if (res.status === 403 && spaToken) {
    spaToken = null;
    spaTokenExpiresAt = 0;
    try { localStorage.removeItem(STORAGE_KEY); localStorage.removeItem(STORAGE_EXPIRY_KEY); } catch { /* noop */ }
    await ensureSpaToken();
    const healHeaders = new Headers(init?.headers);
    applyStandardHeaders(healHeaders);
    res = await fetch(input, { ...init, headers: healHeaders });
  }

  // Retry once on rate limit, respecting Retry-After header
  if (res.status === 429) {
    const retryAfter = parseInt(res.headers.get('Retry-After') || '', 10);
    const delayMs = Math.min((retryAfter || 2) * 1000, 10_000);
    await new Promise(r => setTimeout(r, delayMs));

    await ensureSpaToken();
    const retryHeaders = new Headers(init?.headers);
    applyStandardHeaders(retryHeaders);
    return fetch(input, { ...init, headers: retryHeaders });
  }

  return res;
}

/**
 * Get the current SPA token (or null if not yet acquired).
 * Call ensureSpaToken() first to guarantee it exists.
 */
export function getSpaToken(): string | null {
  return spaToken;
}

/**
 * True once a FRESH token acquisition (no cached token) has been in flight longer than
 * `delayMs` — for showing a "Verifying your browser…" hint during the Turnstile
 * round-trip. Never fires for cache hits (a token already exists → returns false and
 * does nothing); clears as soon as the token lands or fails.
 */
export function useSpaVerifying(delayMs = 2000): boolean {
  const [verifying, setVerifying] = useState(false);
  useEffect(() => {
    if (spaToken) return; // cache hit — instant, never show the hint
    let cancelled = false;
    const timer = setTimeout(() => { if (!cancelled && !spaToken) setVerifying(true); }, delayMs);
    ensureSpaToken().finally(() => { if (!cancelled) { clearTimeout(timer); setVerifying(false); } });
    return () => { cancelled = true; clearTimeout(timer); };
  }, [delayMs]);
  return verifying;
}

export function getApiHost(): string {
  return API_HOST;
}

/** Pre-warm the SPA token (call on app init) */
export function initSpaSession(): void {
  ensureSpaToken().catch(() => {});
}
